import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Button,
  Textarea,
} from "@nfp-tracker/components";
import { Check, CheckCircle2, ChevronLeft, X } from "lucide-react";
import type { PostHogSurvey, PostHogSurveyQuestion } from "../integrations/analytics/client";
import { getNextStepIndex } from "../utils/surveyUtils";

interface PostHogSurveyModalProps {
  survey: PostHogSurvey | null;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (responses: Record<string, unknown>) => void;
}

// ---------------------------------------------------------------------------
// Open ("Other") choice helpers
// ---------------------------------------------------------------------------

/**
 * PostHog marks a choice question as having an open-ended option via
 * `hasOpenChoice`; the open option is always the LAST entry in `choices`.
 * Its submitted value is the text the user typed, not the label.
 */
function splitOpenChoice(question: PostHogSurveyQuestion): {
  openChoice: string | null;
  fixedChoices: string[];
} {
  const choices = question.choices || [];
  if (!question.hasOpenChoice || choices.length === 0) {
    return { openChoice: null, fixedChoices: choices };
  }
  return {
    openChoice: choices[choices.length - 1],
    fixedChoices: choices.slice(0, -1),
  };
}

function OpenChoiceInput({
  value,
  onChange,
  compact,
}: {
  value: string;
  onChange: (value: string) => void;
  compact?: boolean;
}) {
  return (
    <input
      type="text"
      autoFocus
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Tell us more..."
      aria-label="Other — please specify"
      className={`w-full rounded-lg border border-border bg-card px-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 ${
        compact ? "h-9" : "h-10"
      }`}
    />
  );
}

// ---------------------------------------------------------------------------
// Shared question input renderer
// ---------------------------------------------------------------------------

function QuestionInput({
  question,
  currentAnswer,
  onAnswer,
  stepIndex,
  compact = false,
}: {
  question: PostHogSurveyQuestion;
  currentAnswer: unknown;
  onAnswer: (value: unknown) => void;
  stepIndex: number;
  compact?: boolean;
}) {
  switch (question.type) {
    case "rating": {
      const scale = question.scale || 5;
      const scaleItems = Array.from({ length: scale === 10 ? 11 : scale }, (_, i) =>
        scale === 10 ? i : i + 1
      );
      const btnSize = compact
        ? "h-9 w-9 text-xs"
        : "h-10 w-10 sm:h-11 sm:w-11 text-sm";

      return (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-center gap-1.5">
            {scaleItems.map((val) => {
              const isSelected = currentAnswer === val;
              return (
                <button
                  key={val}
                  type="button"
                  onClick={() => onAnswer(val)}
                  className={`flex ${btnSize} items-center justify-center rounded-xl border font-semibold transition-all focus:outline-none focus:ring-2 focus:ring-primary ${
                    isSelected
                      ? "border-primary bg-primary text-primary-foreground shadow-md scale-105"
                      : "border-border bg-card text-foreground hover:border-primary/50 hover:bg-accent"
                  }`}
                >
                  {question.display === "emoji" && scale === 5 ? (
                    <span className="text-base">{["😞", "🙁", "😐", "🙂", "😍"][val - 1]}</span>
                  ) : (
                    val
                  )}
                </button>
              );
            })}
          </div>
          <div className="flex justify-between text-xs text-muted-foreground px-1">
            <span>{question.lowerBoundLabel || "Not satisfied"}</span>
            <span>{question.upperBoundLabel || "Extremely satisfied"}</span>
          </div>
        </div>
      );
    }

    case "single_choice": {
      const choices = question.choices || [];
      const { openChoice, fixedChoices } = splitOpenChoice(question);
      const answerStr = typeof currentAnswer === "string" ? currentAnswer : "";
      // The open choice is selected when the answer is the label itself (nothing
      // typed yet) or any free text that isn't one of the fixed choices.
      const openSelected = Boolean(
        openChoice && answerStr !== "" && !fixedChoices.includes(answerStr)
      );
      const openText = openSelected && answerStr !== openChoice ? answerStr : "";

      return (
        <div className="space-y-2">
          {choices.map((choice) => {
            const isOpen = openChoice !== null && choice === openChoice;
            const isSelected = isOpen ? openSelected : currentAnswer === choice;
            return (
              <div key={choice} className="space-y-2">
                <button
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  onClick={() => onAnswer(choice)}
                  className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left text-sm font-medium transition-all ${
                    isSelected
                      ? "border-primary bg-primary/10 text-primary font-semibold"
                      : "border-border bg-card text-card-foreground hover:bg-accent"
                  }`}
                >
                  <span
                    className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
                      isSelected ? "border-primary" : "border-muted-foreground/40"
                    }`}
                  >
                    {isSelected && <span className="h-2 w-2 rounded-full bg-primary" />}
                  </span>
                  <span>{choice}</span>
                </button>
                {isOpen && isSelected && (
                  <OpenChoiceInput
                    value={openText}
                    compact={compact}
                    onChange={(text) => onAnswer(text === "" ? choice : text)}
                  />
                )}
              </div>
            );
          })}
        </div>
      );
    }

    case "multiple_choice": {
      const choices = question.choices || [];
      const { openChoice, fixedChoices } = splitOpenChoice(question);
      const selectedList: string[] = Array.isArray(currentAnswer)
        ? (currentAnswer as string[])
        : [];
      // Anything selected that isn't a fixed choice is the open-ended answer.
      const openValue = openChoice
        ? selectedList.find((c) => !fixedChoices.includes(c))
        : undefined;
      const openSelected = openValue !== undefined;
      const openText = openValue && openValue !== openChoice ? openValue : "";

      const toggleChoice = (choice: string) => {
        const isOpen = openChoice !== null && choice === openChoice;
        if (isOpen) {
          onAnswer(
            openSelected
              ? selectedList.filter((c) => c !== openValue)
              : [...selectedList, choice]
          );
          return;
        }
        onAnswer(
          selectedList.includes(choice)
            ? selectedList.filter((c) => c !== choice)
            : [...selectedList, choice]
        );
      };

      const setOpenText = (text: string) => {
        const next = text === "" ? (openChoice as string) : text;
        onAnswer(
          selectedList.map((c) => (c === openValue ? next : c))
        );
      };

      return (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">Select all that apply</p>
          {choices.map((choice) => {
            const isOpen = openChoice !== null && choice === openChoice;
            const isSelected = isOpen ? openSelected : selectedList.includes(choice);
            return (
              <div key={choice} className="space-y-2">
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={isSelected}
                  onClick={() => toggleChoice(choice)}
                  className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left text-sm font-medium transition-all ${
                    isSelected
                      ? "border-primary bg-primary/10 text-primary font-semibold"
                      : "border-border bg-card text-card-foreground hover:bg-accent"
                  }`}
                >
                  <span
                    className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                      isSelected
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-muted-foreground/40"
                    }`}
                  >
                    {isSelected && <Check className="h-3 w-3" />}
                  </span>
                  <span>{choice}</span>
                </button>
                {isOpen && isSelected && (
                  <OpenChoiceInput value={openText} compact={compact} onChange={setOpenText} />
                )}
              </div>
            );
          })}
        </div>
      );
    }

    case "open":
    default: {
      const val = typeof currentAnswer === "string" ? currentAnswer : "";
      return (
        <Textarea
          value={val}
          onChange={(e) => onAnswer(e.target.value)}
          placeholder={question.description || "Tell us what you think..."}
          rows={4}
          className="resize-none"
        />
      );
    }
  }
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const PostHogSurveyModal: React.FC<PostHogSurveyModalProps> = ({
  survey,
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [history, setHistory] = useState<number[]>([]);
  const [responses, setResponses] = useState<Record<string, unknown>>({});
  // "snackbar" = Q1 in bottom bar, "dialog" = Q2+ in centered modal, "submitted" = thank you
  const [phase, setPhase] = useState<"snackbar" | "dialog" | "submitted">("snackbar");
  // animate-in flag
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setCurrentStepIndex(0);
      setHistory([]);
      setResponses({});
      setPhase("snackbar");
      // Slight delay so the CSS transition fires on mount
      setTimeout(() => setVisible(true), 20);
    } else {
      setVisible(false);
    }
  }, [isOpen, survey?.id]);

  if (!survey || !isOpen) return null;

  const questions = survey.questions || [];
  const currentQuestion: PostHogSurveyQuestion | undefined = questions[currentStepIndex];

  if (!currentQuestion && phase !== "submitted") return null;

  const qKey = currentQuestion?.id ?? `q_${currentStepIndex}`;
  const currentAnswer = responses[qKey];
  const isAnswered =
    currentAnswer !== undefined && currentAnswer !== null && currentAnswer !== "" &&
    !(Array.isArray(currentAnswer) && currentAnswer.length === 0);
  const isOpenQuestion = currentQuestion?.type === "open";
  const canProceed = Boolean(
    currentQuestion?.optional || isAnswered || (isOpenQuestion && currentQuestion?.optional !== false)
  );

  const nextStepPreview = getNextStepIndex(questions, currentStepIndex, currentAnswer);
  const isFinalStep =
    nextStepPreview === "end" ||
    (typeof nextStepPreview === "number" && nextStepPreview >= questions.length);

  // Answers are keyed purely by question id (or position, for questions with no
  // id). trackSurveySent maps them onto PostHog's `$survey_response_*` keys —
  // adding extra keys here just produces properties PostHog can't display.
  const handleSelectAnswer = (value: unknown) => {
    setResponses((prev) => ({ ...prev, [qKey]: value }));
  };

  const handleProceed = () => {
    const nextStep = getNextStepIndex(questions, currentStepIndex, currentAnswer);

    if (nextStep === "end" || typeof nextStep !== "number" || nextStep >= questions.length) {
      setPhase("submitted");
      onSubmit(responses);
    } else {
      setHistory((prev) => [...prev, currentStepIndex]);
      setCurrentStepIndex(nextStep);
      // Transition from snackbar to dialog when moving past Q1
      if (phase === "snackbar") setPhase("dialog");
    }
  };

  const handleBack = () => {
    if (history.length === 0) return;
    const prevIndex = history[history.length - 1];
    setHistory((prev) => prev.slice(0, -1));
    setCurrentStepIndex(prevIndex);
    if (prevIndex === 0 && phase === "dialog") setPhase("snackbar");
  };

  // -------------------------------------------------------------------------
  // Phase 1 — Snackbar (bottom-center, Q1 only)
  // -------------------------------------------------------------------------
  if (phase === "snackbar") {
    return (
      <div
        role="dialog"
        aria-modal="false"
        aria-label="Quick feedback"
        className={`fixed bottom-6 left-1/2 z-50 w-[calc(100vw-2rem)] max-w-xl -translate-x-1/2 transition-all duration-300 ease-out ${
          visible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
        }`}
      >
        <div className="relative rounded-2xl border border-border bg-card shadow-2xl ring-1 ring-black/5">
          {/* Dismiss button — floats top-right, no header bar */}
          <button
            type="button"
            onClick={onClose}
            aria-label="Dismiss survey"
            className="absolute right-3 top-3 rounded-md p-1 text-muted-foreground/60 transition-colors hover:bg-accent hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <X className="h-3.5 w-3.5" />
          </button>

          {/* Question body */}
          <div className="px-5 pt-5 pb-2 space-y-3 pr-9">
            <p className="text-sm font-medium text-foreground leading-snug">
              {currentQuestion?.question}
            </p>
            {currentQuestion && (
              <QuestionInput
                question={currentQuestion}
                currentAnswer={currentAnswer}
                onAnswer={handleSelectAnswer}
                stepIndex={currentStepIndex}
                compact
              />
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-5 py-3">
            <span className="text-xs text-muted-foreground">
              Takes 5 seconds
            </span>
            <Button
              type="button"
              size="sm"
              disabled={!canProceed}
              onClick={handleProceed}
            >
              {isFinalStep ? (currentQuestion?.buttonText || "Submit") : "Next →"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Phase 2 — Centered Dialog (Q2+ and thank-you)
  // -------------------------------------------------------------------------
  return (
    <Dialog open={phase === "dialog" || phase === "submitted"} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[500px] p-0 gap-0 overflow-hidden">

        {phase === "submitted" ? (
          // Thank-you state
          <div className="flex flex-col items-center justify-center py-10 px-6 text-center space-y-3">
            <CheckCircle2 className="h-12 w-12 text-emerald-500 animate-bounce" />
            <DialogTitle className="text-lg font-bold text-foreground">
              {survey.appearance?.thankYouMessageHeader || "Thank You!"}
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground max-w-xs">
              {survey.appearance?.thankYouMessageDescription ||
                "Your feedback helps us continuously improve NFP Tracker."}
            </DialogDescription>
          </div>
        ) : (
          // Follow-up question
          <div className="px-6 py-5 space-y-4">
            <DialogTitle className="text-base font-medium text-foreground leading-snug">
              {currentQuestion?.question}
            </DialogTitle>
            {currentQuestion?.description && currentQuestion?.type !== "open" ? (
              <DialogDescription className="text-xs text-muted-foreground">
                {currentQuestion.description}
              </DialogDescription>
            ) : (
              // Radix warns when DialogContent has no description; keep one for
              // screen readers even when the question has no description text.
              <DialogDescription className="sr-only">
                Survey question {currentStepIndex + 1} of {questions.length}
              </DialogDescription>
            )}

            {currentQuestion && (
              <QuestionInput
                question={currentQuestion}
                currentAnswer={currentAnswer}
                onAnswer={handleSelectAnswer}
                stepIndex={currentStepIndex}
              />
            )}

            <div className="flex items-center justify-between pt-1">
              {history.length > 0 ? (
                <Button type="button" variant="ghost" size="sm" onClick={handleBack}>
                  <ChevronLeft className="mr-1 h-4 w-4" />
                  Back
                </Button>
              ) : (
                <div />
              )}
              <Button
                type="button"
                variant="default"
                disabled={!canProceed}
                onClick={handleProceed}
              >
                {isFinalStep ? (currentQuestion?.buttonText || "Submit") : "Next"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
